/**
 * Analysis Header Card
 *
 * Tab navigation and date range selector for Trading Analysis.
 * Follows the Portfolio header pattern but more compact.
 */

import { cn } from "@/lib/utils";
import {
  DateRangeSelector,
  type DateRangeValue,
} from "@/components/ui/date-range-selector";

export type AnalysisTab =
  | "overview"
  | "performance"
  | "calendar"
  | "time"
  | "assets"
  | "behavior"
  | "achievements"
  | "review";

interface AnalysisHeaderCardProps {
  activeTab: AnalysisTab;
  onTabChange: (tab: AnalysisTab) => void;
  dateRange: DateRangeValue;
  onDateRangeChange: (range: DateRangeValue) => void;
  isSticky?: boolean;
}

const TABS: { value: AnalysisTab; label: string; badge?: string }[] = [
  { value: "overview", label: "Overview" },
  { value: "performance", label: "Performance" },
  { value: "calendar", label: "Calendar" },
  { value: "time", label: "Time" },
  { value: "assets", label: "Assets" },
  { value: "behavior", label: "Behavior" },
  { value: "achievements", label: "Achievements" },
  { value: "review", label: "Review", badge: "Beta" },
];

export function AnalysisHeaderCard({
  activeTab,
  onTabChange,
  dateRange,
  onDateRangeChange,
  isSticky = false,
}: AnalysisHeaderCardProps) {
  return (
    <div
      className={cn(
        "rounded-lg bg-card border border-border/50 shadow-sm transition-all duration-300",
        isSticky
          ? "sticky top-0 z-50 py-2 px-3 md:px-5 backdrop-blur-sm bg-card/95"
          : "p-3 md:px-5 md:py-4"
      )}
    >
      {/* Mobile Layout */}
      <div className="lg:hidden space-y-3">
        {/* Title row */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-foreground">Performance Lab Pro™</h1>
            <p
              className={cn(
                "text-muted-foreground text-sm transition-all duration-300 overflow-hidden",
                isSticky ? "max-h-0 opacity-0 mt-0" : "max-h-10 opacity-100 mt-0.5"
              )}
            >
              Analyze. Optimize. Dominate.
            </p>
          </div>
          <DateRangeSelector
            value={dateRange}
            onChange={onDateRangeChange}
          />
        </div>

        {/* Tabs - horizontally scrollable */}
        <div className="flex items-center gap-1 p-1 bg-muted/50 rounded-lg overflow-x-auto scrollbar-none">
          {TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => onTabChange(tab.value)}
              className={cn(
                "px-3 py-1.5 text-sm font-medium rounded-md transition-colors whitespace-nowrap flex-shrink-0 flex items-center gap-1",
                activeTab === tab.value
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted"
              )}
            >
              {tab.label}
              {tab.badge && (
                <span className="px-1 py-0.5 text-[8px] font-medium bg-primary/20 text-primary rounded">
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="hidden lg:flex items-center justify-between">
        {/* Title */}
        <div>
          <h1 className="text-lg font-bold text-foreground">Performance Lab Pro™</h1>
          <p
            className={cn(
              "text-muted-foreground text-sm transition-all duration-300 overflow-hidden",
              isSticky ? "max-h-0 opacity-0 mt-0" : "max-h-10 opacity-100 mt-0.5"
            )}
          >
            Analyze. Optimize. Dominate.
          </p>
        </div>

        {/* Tabs (centered) */}
        <div className="flex items-center gap-1 p-1 bg-muted/50 rounded-lg">
          {TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => onTabChange(tab.value)}
              className={cn(
                "px-4 py-1.5 text-sm font-medium rounded-md transition-all flex items-center gap-1",
                activeTab === tab.value
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted"
              )}
            >
              {tab.label}
              {tab.badge && (
                <span className={cn(
                  "px-1 py-0.5 text-[8px] font-medium rounded",
                  activeTab === tab.value
                    ? "bg-primary-foreground/20 text-primary-foreground"
                    : "bg-primary/20 text-primary"
                )}>
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Date Range */}
        <DateRangeSelector
          value={dateRange}
          onChange={onDateRangeChange}
        />
      </div>
    </div>
  );
}
